/**
 * OneTrust -> Zaraz consent bridge.
 *
 * OneTrust (FlexyConsent) exposes the visitor's active category IDs via
 * window.OnetrustActiveGroups and fires callbacks when they change. Zaraz
 * exposes zaraz.consent.set()/sendQueuedEvents(). This script maps the two
 * so Zaraz consent mode follows OneTrust choices without depending on a
 * Zaraz Custom HTML tool.
 *
 * Purpose IDs come from Zaraz -> Consent:
 *   jCtP = Analytics (OneTrust C0002)
 *   mGtt = Marketing (OneTrust C0004)
 */
export const ZARAZ_CONSENT_BRIDGE = `(function () {
  var MAP = {
    C0002: "jCtP",
    C0004: "mGtt"
  };
  var queuedSent = false;
  var lastPurposeStatus = "";
  var X_KEY = "rh_consent_banner_x";
  var I_KEY = "rh_consent_interacted";

  // Google Consent Mode v2 default - deny everything.
  var DENIED_DEFAULT = {
    "ad_storage": "denied",
    "ad_user_data": "denied",
    "ad_personalization": "denied",
    "analytics_storage": "denied"
  };
  function setGoogleDefault() {
    if (window.zaraz && typeof zaraz.set === "function") {
      zaraz.set("google_consent_default", DENIED_DEFAULT);
      return true;
    }
    return false;
  }
  setGoogleDefault();

  // Consent is granted ONLY by an explicit OneTrust action:
  //  1. Closing the banner with its X button -> Analytics + Marketing granted.
  //  2. Saving choices in Privacy Preferences / Accept -> whatever groups
  //     OneTrust reports as enabled.
  //  3. Clicking, tapping or pressing a key elsewhere on the page before any
  //     explicit choice (OneTrust accept-on-click) -> Analytics + Marketing granted.
  // Scrolling alone never grants consent. An explicit Reject always wins.
  function bannerClosed() {
    return document.cookie.indexOf("OptanonAlertBoxClosed") !== -1;
  }
  function closedWithX() {
    try { return window.localStorage.getItem(X_KEY) === "1"; } catch (e) { return false; }
  }
  function setClosedWithX(v) {
    try {
      if (v) window.localStorage.setItem(X_KEY, "1");
      else window.localStorage.removeItem(X_KEY);
    } catch (e) {}
  }

  function interacted() {
    try { return window.localStorage.getItem(I_KEY) === "1"; } catch (e) { return false; }
  }
  function setInteracted(v) {
    try {
      if (v) window.localStorage.setItem(I_KEY, "1");
      else window.localStorage.removeItem(I_KEY);
    } catch (e) {}
  }

  // If OneTrust has no saved decision (banner showing), any leftover flags
  // from an earlier visit are stale (e.g. cookies cleared but localStorage
  // kept). Clear them so consent never auto-grants on page load.
  if (!bannerClosed()) {
    setInteracted(false);
    setClosedWithX(false);
  }

  function denyAll() {
    if (!(window.zaraz && zaraz.consent && zaraz.consent.APIReady)) return;
    var status = {};
    for (var group in MAP) status[MAP[group]] = false;
    var serialized = JSON.stringify(status);
    if (serialized === lastPurposeStatus) return;
    zaraz.consent.set(status);
    lastPurposeStatus = serialized;
  }

  function sync() {
    if (!bannerClosed() && !interacted()) {
      denyAll();
      return;
    }
    var groups;
    if (closedWithX() || interacted()) {
      groups = Object.keys(MAP);
    } else {
      groups = (window.OnetrustActiveGroups || "").split(",").filter(Boolean);
    }
    var status = {};
    var anyGranted = false;
    for (var group in MAP) {
      var granted = groups.indexOf(group) !== -1;
      status[MAP[group]] = granted;
      if (granted) anyGranted = true;
    }

    // Google consent update is independent of Zaraz API readiness so the
    // gcd parameter is emitted even before Zaraz's consent API is ready.
    var marketingGranted = groups.indexOf("C0004") > -1;
    var analyticsGranted = groups.indexOf("C0002") > -1;
    var consentUpdate = {
      ad_storage: marketingGranted ? "granted" : "denied",
      ad_user_data: marketingGranted ? "granted" : "denied",
      ad_personalization: marketingGranted ? "granted" : "denied",
      analytics_storage: analyticsGranted ? "granted" : "denied"
    };
    if (window.zaraz && typeof zaraz.set === "function") {
      zaraz.set("google_consent_update", consentUpdate);
    }

    if (!(window.zaraz && zaraz.consent && zaraz.consent.APIReady)) return;
    var serialized = JSON.stringify(status);
    if (serialized !== lastPurposeStatus) {
      zaraz.consent.set(status);
      lastPurposeStatus = serialized;
    }

    if (anyGranted && !queuedSent) {
      queuedSent = true;
      try { zaraz.consent.sendQueuedEvents(); } catch (e) {}
    }
  }

  // Track which OneTrust button made the choice. Capture phase so we record
  // it before OneTrust hides the banner and fires its callbacks.
  document.addEventListener("click", function (event) {
    if (!event.isTrusted) return;
    var t = event.target;
    if (!t || typeof t.closest !== "function") return;
    if (t.closest("#onetrust-banner-sdk .onetrust-close-btn-handler, #onetrust-banner-sdk #onetrust-close-btn-container button")) {
      setClosedWithX(true);
    } else if (t.closest("#onetrust-accept-btn-handler, #onetrust-reject-all-handler, #accept-recommended-btn-handler, .save-preference-btn-handler, .ot-pc-refuse-all-handler")) {
      // Explicit Accept / Reject / Save in preferences overrides an earlier X.
      setClosedWithX(false);
      setInteracted(false);
    } else if (t.closest("#onetrust-consent-sdk")) {
      // Other clicks inside OneTrust UI (opening preferences, toggles) are not consent.
      return;
    } else {
      // OneTrust is configured as accept-on-click: a click/tap anywhere else
      // grants consent, but only while no explicit choice has been made yet.
      if (bannerClosed() && !interacted()) return;
      setInteracted(true);
    }
    setTimeout(sync, 0);
    setTimeout(sync, 300);
  }, true);

  document.addEventListener("keydown", function (event) {
    if (!event.isTrusted) return;
    var t = event.target;
    if (t && typeof t.closest === "function" && t.closest("#onetrust-consent-sdk")) return;
    if (bannerClosed() && !interacted()) return;
    if (interacted()) return;
    setInteracted(true);
    setTimeout(sync, 0);
    setTimeout(sync, 300);
  }, true);

  var prior = window.OptanonWrapper;
  window.OptanonWrapper = function () {
    if (typeof prior === "function") prior();
    sync();
  };
  window.addEventListener("OneTrustGroupsUpdated", sync);
  document.addEventListener("zarazConsentAPIReady", function () {
    // The first head call can precede Zaraz. Reapply the reserved default
    // once its API is ready so Google requests receive a complete gcd state.
    setGoogleDefault();
    denyAll();
    sync();
  });
  denyAll();
  sync();
})();`;
